// src/utils/genotyper.ts
import type { GenotyperRunStatus, GenotyperRunWarning } from "../types/lab/GenotyperTypes";

// Mirrors labiq's App\Genotyper\RunStatus::TERMINAL — polling stops on any of these.
export const GENOTYPER_TERMINAL_STATUSES: readonly GenotyperRunStatus[] = ["completed", "failed", "partial"];

export const isTerminalRunStatus = (status: string | null | undefined): boolean =>
	GENOTYPER_TERMINAL_STATUSES.includes(status as GenotyperRunStatus);

export const GENOTYPER_RUN_STATUS_LABELS: Record<GenotyperRunStatus, string> = {
	queued: "Queued",
	extracting: "Extracting",
	snp_import: "SNP import",
	analysis: "Analysis",
	loading: "Loading",
	completed: "Completed",
	failed: "Failed",
	partial: "Partial",
};

// Below this Illumina call rate the sample is flagged, matching the lab's own QC threshold.
export const CALL_RATE_WARNING_THRESHOLD = 0.98;

// Laravel serialises the decimal column as a string, so a string is accepted here.
export const parseCallRate = (rate: number | string | null | undefined): number | null => {
	if (rate === null || rate === undefined || rate === "") return null;
	const value = typeof rate === "number" ? rate : Number(rate);
	return Number.isFinite(value) ? value : null;
};

export const formatCallRate = (rate: number | string | null | undefined): string => {
	const value = parseCallRate(rate);
	return value === null ? "—" : `${(value * 100).toFixed(2)} %`;
};

export const isLowCallRate = (rate: number | string | null | undefined): boolean => {
	const value = parseCallRate(rate);
	return value !== null && value < CALL_RATE_WARNING_THRESHOLD;
};

export const sentrixLabel = (barcode: string | null | undefined, position: string | null | undefined): string =>
	barcode && position ? `${barcode}_${position}` : barcode || position || "—";

// Decision 5 (2026-09-21): a no_signal CNV is a legitimate empty result, not a failure, and the tooltip says why.
export const NO_SIGNAL_TOOLTIP =
	"PennCNV produced no calls — LRR/BAF were NaN. No CNV results were stored for this sample.";

const ERROR_CODE_HINTS: Record<string, string> = {
	unknown_sample: "A Sample_ID in the sheet is not in this group",
	idat_missing: "The bundle is missing an IDAT pair for a sample in the sheet",
	reference_missing: "The reference cache is missing on this pod — ask an administrator to check genotyper:doctor",
	missing: "The reference cache is missing on this pod — ask an administrator to check genotyper:doctor",
	checksum_mismatch: "checksums.md5 does not match the extracted files",
};

// Matches on the suffix after the last dot (`genotyper.bundle.idat_missing` → `idat_missing`); unknown codes come back raw.
export const errorCodeHint = (code: string | null | undefined): string => {
	if (!code) return "";
	const suffix = code.slice(code.lastIndexOf(".") + 1);
	return ERROR_CODE_HINTS[suffix] ?? code;
};

const WARNING_LABELS: Record<string, string> = {
	missing_from_sheet: "Not in the sample sheet",
	idat_missing: "IDAT pair missing from the bundle",
	tsv_missing: "A results TSV was missing from the pipeline output",
	no_finalreports: "No FinalReport files were found in the bundle",
};

export const warningLabel = (code: string): string => WARNING_LABELS[code] ?? code;

export const warningHint = (code: string): string | undefined => WARNING_LABELS[code];

// The code plus whichever extras the writer attached: `missing_from_sheet · SMT0001`, `tsv_missing · cnv · 210741980104`, or the bare code.
export const describeWarning = (warning: GenotyperRunWarning): string =>
	[warning.code, warning.sample_id, warning.table, warning.barcode].filter((part): part is string => !!part).join(" · ");

export const GSA_RAW_BUNDLE_MESSAGE = "GSA Raw expects exactly one .zip — the AGRF delivery folder zipped as received";

const ZIP_MIME_TYPES = ["application/zip", "application/x-zip-compressed", ""];

export const isZipFile = (file: Pick<File, "name" | "type">): boolean =>
	/\.zip$/i.test(file.name) && ZIP_MIME_TYPES.includes(file.type);

// Client-side twin of labiq's `files|size:1` + `mimes:zip` rules for a gsa_raw upload.
export const validateGsaRawBundle = (files: Array<Pick<File, "name" | "type">>): string | null =>
	files.length === 1 && isZipFile(files[0]) ? null : GSA_RAW_BUNDLE_MESSAGE;
